import { EntityType, RecentActivityItem } from "../services/types/recentActivityTypes";

interface ParsedEntityInfo {
    entityType: EntityType;
    entityId: string;
    entityName: string;
    subtitle?: string;
}

/**
 * Parses event data into entity information based on the activity type.
 * Returns null if the data cannot be parsed or is missing required fields.
 *
 * @param activityType - The activity type from the event type (session, resource, user, registration)
 * @param eventTag - The event tag that triggered this activity
 * @param data - The event data object (already parsed from JSON)
 */
export function parseEntityFromEventData(
    activityType: string,
    eventTag: string,
    data: Record<string, any>
): ParsedEntityInfo | null {
    switch (activityType) {
        case "session":
            return parseSessionActivity(data);
        case "resource":
            return parseResourceActivity(data);
        case "user":
            return parseUserActivity(data);
        case "registration":
            return parseRegistrationActivity(data);
        default:
            return null;
    }
}

/**
 * Parse session-related event data.
 * Expected fields: sessionID, projectCode
 */
function parseSessionActivity(data: Record<string, any>): ParsedEntityInfo | null {
    const sessionId = data.sessionID ?? data.sessionId ?? data.SessionID;
    if (!sessionId) return null;

    const projectCode = data.projectCode ?? data.ProjectCode;
    const displayName = projectCode || `Session ${sessionId}`;

    return {
        entityType: "session",
        entityId: String(sessionId),
        entityName: displayName,
        subtitle: displayName,
    };
}

/**
 * Parse resource-related event data.
 * Expected fields: imKey, email
 *
 * Note: This returns imKey as entityId, but useLogAccessEvent will fetch
 * the pkey via getPkeyByImKey and replace entityId with the pkey before
 * storing to Redux. This is because resource URLs require pkey, not imKey.
 */
function parseResourceActivity(data: Record<string, any>): ParsedEntityInfo | null {
    const imKey = data.imKey ?? data.ImKey ?? data.imkey;
    if (!imKey) return null;

    const email = data.email ?? data.Email;

    return {
        entityType: "resource",
        entityId: String(imKey),
        entityName: email ?? `Resource ${imKey}`,
        subtitle: email,
    };
}

/**
 * Parse user-related event data.
 * Expected fields: imKey, email
 */
function parseUserActivity(data: Record<string, any>): ParsedEntityInfo | null {
    const imKey = data.imKey ?? data.ImKey ?? data.imkey;
    if (!imKey) return null;

    const email = data.email ?? data.Email;

    return {
        entityType: "user",
        entityId: String(imKey),
        entityName: email ?? `User ${imKey}`,
        subtitle: email,
    };
}

/**
 * Parse registration-related event data.
 * Expected fields: esiKey, email, projectCode
 */
function parseRegistrationActivity(data: Record<string, any>): ParsedEntityInfo | null {
    const esiKey = data.esiKey ?? data.EsiKey ?? data.esikey;
    if (!esiKey) return null;

    const email = data.email ?? data.Email;
    const projectCode = data.projectCode ?? data.ProjectCode;

    return {
        entityType: "registration",
        entityId: String(esiKey),
        entityName: email ?? projectCode ?? `Registration ${esiKey}`,
        subtitle: projectCode ?? email,
    };
}

/**
 * Creates a full RecentActivityItem from parsed entity info and event metadata.
 */
export function createRecentActivityItem(
    entityInfo: ParsedEntityInfo,
    eventTag: string
): RecentActivityItem {
    return {
        ...entityInfo,
        lastAccessedUtc: new Date().toISOString(),
        eventType: eventTag,
    };
}
